import { useCallback } from 'react';
import introJs from 'intro.js';
import 'intro.js/introjs.css';
import './tour-theme.css';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authUserStore';
import { getTourSteps } from './tourSteps';

const TOUR_SEEN_KEY = 'cupcycle_tour_seen';

function markTourSeen(role: string) {
  const seen = JSON.parse(localStorage.getItem(TOUR_SEEN_KEY) || '{}');
  seen[role] = true;
  localStorage.setItem(TOUR_SEEN_KEY, JSON.stringify(seen));
}

export function hasTourBeenSeen(role: string): boolean {
  const seen = JSON.parse(localStorage.getItem(TOUR_SEEN_KEY) || '{}');
  return !!seen[role];
}

export function useTour() {
  const { t } = useTranslation();
  const currentUser = useAuthStore((s) => s.currentUser);

  const startTour = useCallback(() => {
    const role = currentUser?.role || 'admin';
    const steps = getTourSteps(role, t, window.location.pathname);

    function findVisible(selector: string): Element | null {
      const matches = document.querySelectorAll(selector);
      for (let i = 0; i < matches.length; i++) {
        const rect = matches[i].getBoundingClientRect();
        if (rect.width > 0 || rect.height > 0) return matches[i];
      }
      return null;
    }

    const validSteps = steps
      .map((step) => {
        if (!step.element) return step;
        const el = findVisible(step.element);
        if (!el) return null;
        return { ...step, element: el as HTMLElement };
      })
      .filter((s): s is NonNullable<typeof s> => s !== null);

    if (validSteps.length === 0) return;

    const intro = introJs();
    const syncTourViewport = () => {
      window.requestAnimationFrame(() => {
        const activeElement = document.querySelector('.introjs-showElement') as HTMLElement | null;
        activeElement?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });

        window.requestAnimationFrame(() => {
          intro.refresh();

          const tooltipLayer = document.querySelector('.introjs-tooltipReferenceLayer') as HTMLElement | null;
          const focusTarget = tooltipLayer?.querySelector(
            '.introjs-nextbutton, .introjs-donebutton, .introjs-prevbutton, .introjs-skipbutton'
          ) as HTMLElement | null;

          if (tooltipLayer) {
            const rect = tooltipLayer.getBoundingClientRect();
            const viewportPadding = 16;
            if (rect.top < viewportPadding || rect.bottom > window.innerHeight - viewportPadding) {
              const offset = rect.top < viewportPadding
                ? rect.top - viewportPadding
                : rect.bottom - window.innerHeight + viewportPadding;

              window.scrollBy({ top: offset, behavior: 'auto' });
              window.requestAnimationFrame(() => intro.refresh());
            }
          }

          focusTarget?.focus({ preventScroll: true });
        });
      });
    };

    intro.setOptions({
      steps: validSteps,
      showProgress: true,
      showBullets: true,
      exitOnOverlayClick: true,
      doneLabel: t('tour.btn.done'),
      nextLabel: t('tour.btn.next'),
      prevLabel: t('tour.btn.prev'),
      scrollToElement: true,
      overlayOpacity: 0.6,
    });

    intro.onafterchange(syncTourViewport);
    intro.oncomplete(() => markTourSeen(role));
    intro.onexit(() => markTourSeen(role));

    intro.start();
    syncTourViewport();
  }, [currentUser, t]);

  return { startTour };
}
